import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import Keycloak from 'keycloak-js';
import { attachKeycloak, clearBearerToken, detachKeycloak } from '../api/axiosInstance';
import type {
  AuthState,
  AuthUser,
  TokenClaims,
  OrganizationData,
} from '../types/auth';

/**
 * Keycloak instance - singleton at module level to prevent re-initialization
 */
const keycloak = new Keycloak({
  url: import.meta.env.VITE_KEYCLOAK_URL,
  realm: import.meta.env.VITE_KEYCLOAK_REALM,
  clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID,
});

/**
 * Client ID for role extraction — resolves per app from the environment.
 */
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'portal';

const DEFAULT_SCOPE = 'openid profile email organization';

/**
 * A session ending in one tab — manual or idle-triggered (specs/059-idle-session-timeout,
 * FR-007) — must end it in every other tab. Both paths already call logout() below, so the
 * broadcast lives here rather than in the idle-timeout feature's own code, which a manual
 * "Log out" click never runs through.
 */
const SESSION_CHANNEL_NAME = 'claimos-auth-session';
type SessionChannelMessage = { type: 'session-ended' };

/**
 * Auth context for app-wide authentication state
 * Using undefined as default to detect usage outside provider
 */
const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Extract user info from Keycloak token claims
 */
function extractUserFromToken(
  tokenParsed: TokenClaims,
  requireOrganization = true
): AuthUser | null {
  const { organization, resource_access, sub, email, given_name, family_name, preferred_username } =
    tokenParsed;

  const clientRolesFor = (id: string) => resource_access?.[id]?.roles ?? [];
  const displayName =
    given_name && family_name ? `${given_name} ${family_name}` : preferred_username || email;

  if (!organization || Object.keys(organization).length === 0) {
    // Internal staff hold no organization membership and are never tenant-scoped, so an
    // absent claim is their normal state rather than a failure to resolve one.
    if (!requireOrganization) {
      return {
        id: sub,
        email,
        name: displayName,
        orgId: '',
        tenantId: '',
        directory: '',
        organizationName: '',
        roles: clientRolesFor(CLIENT_ID),
      };
    }
    console.error('[AuthContext] No organization claim in token');
    return null;
  }

  const [organizationSlug, orgData] = Object.entries(organization)[0] as [
    string,
    OrganizationData,
  ];

  // Extract tenant_id and directory from org attributes (arrays in Keycloak)
  // These are optional - org may not have them configured yet
  const tenantId = orgData.tenant_id?.[0] ?? orgData.id;
  const directory = orgData.directory?.[0] ?? organizationSlug;
  // Use display name from org data, fall back to slug
  const organizationName = orgData.display_name?.[0] ?? organizationSlug;

  const clientRoles = clientRolesFor(CLIENT_ID);

  return {
    id: sub,
    email,
    name: displayName,
    orgId: orgData.id,
    tenantId,
    directory,
    organizationName,
    roles: clientRoles,
  };
}

/**
 * AuthProvider props
 */
interface AuthProviderProps {
  children: ReactNode;
  /** When false, a token with no organization claim yields a user with empty org/tenant fields. */
  requireOrganization?: boolean;
  /** OIDC scopes requested at init. */
  scope?: string;
}

/**
 * Authentication provider component.
 * Wraps the app and provides auth state via context.
 */
export function AuthProvider({
  children,
  requireOrganization = true,
  scope = DEFAULT_SCOPE,
}: AuthProviderProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [user, setUser] = useState<AuthUser | undefined>();
  const initialized = useRef(false);
  // Track refresh in progress to prevent race between axios interceptor and onTokenExpired
  const refreshInProgress = useRef(false);
  const sessionChannelRef = useRef<BroadcastChannel | null>(null);
  // Synced idle timers fire in every tab at once, so a tab both calls logout() and hears every
  // other tab's session-ended. Each extra keycloak.logout() is another end-session request for a
  // session already gone, which Keycloak rejects as session_expired.
  const signingOutRef = useRef(false);

  useEffect(() => {
    const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<SessionChannelMessage>) => {
      if (event.data?.type === 'session-ended' && !signingOutRef.current) {
        // Ends this tab's session too, without re-posting — logout() below is what posts,
        // and calling it again here would loop the broadcast back and forth between tabs.
        signingOutRef.current = true;
        keycloak.logout();
      }
    };
    sessionChannelRef.current = channel;

    return () => {
      channel.close();
      sessionChannelRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    // Set up error callbacks BEFORE init()
    keycloak.onAuthError = () => {
      setIsError(true);
      setErrorMessage('Authentication failed. Please try again.');
      setIsLoading(false);
    };

    keycloak.onAuthRefreshError = () => {
      // Token refresh failed - redirect to login
      keycloak.login();
    };

    keycloak.onTokenExpired = () => {
      // Avoid race with axios request interceptor's proactive refresh
      if (refreshInProgress.current) {
        return;
      }
      refreshInProgress.current = true;

      // Token refresh is handled proactively by axios request interceptor.
      // This handler is a fallback in case a request isn't made before expiration.
      keycloak
        .updateToken(30)
        .then((refreshed) => {
          if (refreshed && keycloak.tokenParsed) {
            // Update all auth state for consistency
            const updatedUser = extractUserFromToken(
              keycloak.tokenParsed as TokenClaims,
              requireOrganization
            );
            if (updatedUser) {
              setUser(updatedUser);
              setIsAuthenticated(true);
              setIsError(false);
              setErrorMessage(undefined);
            }
          }
        })
        .catch(() => {
          // Clear all auth state before redirect to prevent stale data display
          clearBearerToken();
          detachKeycloak();
          setIsAuthenticated(false);
          setUser(undefined);
          setIsError(false);
          setErrorMessage(undefined);
          // Refresh failed - redirect to login
          keycloak.login();
        })
        .finally(() => {
          // Always reset the guard, even if extractUserFromToken throws
          refreshInProgress.current = false;
        });
    };

    // Which identity provider a realm sends the user to is the realm's business, not the
    // SPA's: realms whose users are all federated set a default provider on the browser
    // flow, so Keycloak redirects without being asked.
    keycloak
      .init({
        onLoad: 'login-required',
        scope,
      })
      .then((authenticated) => {
        setIsAuthenticated(authenticated);

        if (authenticated && keycloak.token && keycloak.tokenParsed) {
          // Attach Keycloak instance to axios for proactive token refresh
          attachKeycloak(keycloak);

          const extractedUser = extractUserFromToken(
            keycloak.tokenParsed as TokenClaims,
            requireOrganization
          );

          if (extractedUser) {
            setUser(extractedUser);
          } else {
            // User has no valid organization - mark as not authenticated
            clearBearerToken();
            setIsAuthenticated(false);
            setIsError(true);
            setErrorMessage('NO_ORGANIZATION');
          }
        }
      })
      .catch((error) => {
        console.error('Keycloak init failed:', error);
        setIsError(true);
        setErrorMessage('SERVICE_UNAVAILABLE');
      })
      .finally(() => {
        setIsLoading(false);
      });
    // The initialized ref keeps this to a single run; the deps only satisfy the linter.
  }, [requireOrganization, scope]);

  const authState: AuthState = {
    isAuthenticated,
    isLoading,
    isError,
    errorMessage,
    user,
    login: () => keycloak.login(),
    logout: () => {
      if (signingOutRef.current) return;
      signingOutRef.current = true;
      sessionChannelRef.current?.postMessage({ type: 'session-ended' } satisfies SessionChannelMessage);
      keycloak.logout();
    },
  };

  return (
    <AuthContext.Provider value={authState}>{children}</AuthContext.Provider>
  );
}

/**
 * Hook to access authentication state.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
